import { useQuery, useQueryClient } from '@tanstack/react-query';

import { bankFeedApi } from 'domain/bankFeed/api';
import type { EndpointBody } from 'services/api';
import { queryOptions, useApiMutation } from 'services/query';
import { handleErrorResponseWithToast, yieldSuccessNotification } from 'lib/toasts';
import { bankFeedKeys } from './keys';

export const bankFeedSyncSchedulesQuery = (companyId: string) =>
  queryOptions({
    queryKey: bankFeedKeys.syncSchedules(companyId),
    queryFn: () => bankFeedApi.getSyncSchedules({ path: { companyId } }),
  });

export const useBankFeedSyncSchedules = (companyId: string) => useQuery(bankFeedSyncSchedulesQuery(companyId));

// Scope closure: companyId is part of the queryKey (mirrors useBankFeedSyncSchedules)
// so the hook fills it into the endpoint call. Body still passes through under
// the `body:` key — endpoint shape is preserved at the caller.
export const useUpdateBankFeedSyncSchedulesMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (vars: { body: EndpointBody<typeof bankFeedApi.updateSyncSchedules> }) =>
      bankFeedApi.updateSyncSchedules({ path: { companyId }, body: vars.body }),
    onSuccess: (response) => {
      qc.setQueryData(bankFeedKeys.syncSchedules(companyId), response);
      yieldSuccessNotification({
        id: 'configurations.company.bankFeed.tabs.sync.toast.update.success',
        defaultMessage: 'Sync settings successfully updated',
      });
    },
    onError: handleErrorResponseWithToast,
  });
};
